import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./Select.module.css";
import { getClassName } from "../../utils/className.js";

const SHOW_DELAY_MS = 250;
const VIEWPORT_MARGIN = 4;
const ANCHOR_GAP = 6;

/**
 * SelectTooltip renders hover/focus tooltip text for a Select trigger in a `document.body`
 * portal with viewport-aware fixed positioning, so it can't be clipped or covered by
 * ancestors with `overflow: hidden` or their own stacking context.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.text] - Tooltip text. If falsy, nothing is rendered/attached.
 * @param {boolean} [props.light] - If true, renders the tooltip with an inverted color scheme (white background, dark text) instead of the default dark bubble.
 * @param {React.RefObject<HTMLElement>} props.anchorRef - Reference to the element the tooltip is anchored to.
 */
const SelectTooltip = ({ text, light, anchorRef }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const bubbleRef = useRef(null);
  const showTimeoutRef = useRef(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;

    const anchorRect = anchor.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    let top = anchorRect.top - bubbleRect.height - ANCHOR_GAP;
    if (top < VIEWPORT_MARGIN) {
      top = anchorRect.bottom + ANCHOR_GAP;
    }

    const maxLeft = window.innerWidth - bubbleRect.width - VIEWPORT_MARGIN;
    const left = Math.min(Math.max(anchorRect.left, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, maxLeft));

    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
  }, [visible, text, updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !text) return undefined;

    const handleShow = () => {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };
    const handleHide = () => {
      clearTimeout(showTimeoutRef.current);
      setVisible(false);
    };

    anchor.addEventListener("mouseenter", handleShow);
    anchor.addEventListener("mouseleave", handleHide);
    anchor.addEventListener("focus", handleShow);
    anchor.addEventListener("blur", handleHide);

    return () => {
      clearTimeout(showTimeoutRef.current);
      anchor.removeEventListener("mouseenter", handleShow);
      anchor.removeEventListener("mouseleave", handleHide);
      anchor.removeEventListener("focus", handleShow);
      anchor.removeEventListener("blur", handleHide);
    };
  }, [anchorRef, text]);

  if (!text || typeof document === "undefined") return null;

  return createPortal(
    <span
      ref={bubbleRef}
      role="tooltip"
      className={getClassName(styles.tooltipBubble, "select-tooltip", {
        [styles.tooltipBubbleLight]: light,
        [styles.tooltipBubbleVisible]: visible && !!pos,
      })}
      style={{ top: pos ? pos.top : -9999, left: pos ? pos.left : -9999 }}
    >
      {text}
    </span>,
    document.body
  );
};

export default SelectTooltip;
